import {
  useContext,
  useState,
  type ChangeEvent,
  type ComponentType,
} from "react";
import Card from "../UI/Card";
import { Table } from "../UI/Table";
import Button from "../UI/Button";
import Text from "../UI/Text";
import useAutoActions from "../../hooks/use-auto-actions";
// @ts-expect-error pure js
import ToggleSwitch from "../UI/ToggleSwitch";
// @ts-expect-error pure js
import useAutoActionsAssociations from "../../hooks/use-auto-actions-associations";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";
// @ts-expect-error pure js
import { useConfirm } from "../../hooks/use-confirm";
import { ProfileRightsContext } from "../../store/profile-rights-context";
import { TenantLeaseType } from "../../utils/constants/tenants";
// @ts-expect-error pure js
import { AutoActionType } from "../../utils/constants/autoActions";
import {
  AlerteVirement,
  Assurance,
  AvisEcheance,
  EntretienChauffage,
  ImpayeIcon,
  QuittanceIcon,
  ResiliationBail,
  RevisionLoyerIcon,
  // @ts-expect-error pure js
} from "../UI/Icons";
import Tooltip from "../UI/Tooltip";
// @ts-expect-error pure js
import { getFormattedCommentaires } from "../../utils/actions";
import useTableSort from "../../hooks/use-table-sort";
import TenantPropertyCell from "./TenantPropertyCell";
import type { Tenant } from "../../models/tenant";
import styles from "./MassAutoActions.module.css";

type BulkMode = "activate" | "deactivate";

interface BulkActionItem {
  id_tenant: number;
  nom_auto_action: string;
}

interface BulkConfirmInput {
  mode: BulkMode;
  staticIds: string[];
}

interface PendingBulk {
  mode: BulkMode;
  count: number;
}

interface MassAutoActionsProps {
  tenants: Tenant[];
}

interface ActionTitle {
  icon: ComponentType;
  label: string;
}

const actionTitles: Record<string, ActionTitle> = {
  quittance: { icon: QuittanceIcon, label: "Quittance de loyer" },
  avis_echeance: { icon: AvisEcheance, label: "Avis d'échéance" },
  revision: { icon: RevisionLoyerIcon, label: "Révision de loyer" },
  assurance: { icon: Assurance, label: "Assurance locataire" },
  attestation_chauffage: {
    icon: EntretienChauffage,
    label: "Entretien chauffage",
  },
  resiliation_bail: { icon: ResiliationBail, label: "Résiliation du bail" },
  retard_loyer: { icon: ImpayeIcon, label: "Impayé de loyer" },
  receive_loyers: { icon: AlerteVirement, label: "Alerte virement" },
};

function isActionAllowed(tenant: Tenant, staticId: string): boolean {
  if (
    tenant.lease_type !== undefined &&
    [TenantLeaseType.MOBILITE, TenantLeaseType.LCD].includes(
      tenant.lease_type,
    ) &&
    [AutoActionType.RESILIATION_BAIL, AutoActionType.REVISION_LOYER].includes(
      staticId,
    )
  ) {
    return false;
  }
  return !(
    staticId === AutoActionType.ATTESTATION_CHAUFFAGE &&
    !TenantLeaseType.isHabitation(tenant.lease_type)
  );
}

export default function MassAutoActions({ tenants }: MassAutoActionsProps) {
  const { autoActions } = useAutoActions();
  const {
    autoActionsAssociations,
    createAutoAction,
    deleteAutoAction,
    bulkCreateAutoActions,
    bulkDeleteAutoActions,
    mutate: refresh,
  } = useAutoActionsAssociations();
  const { propertiesById } = useProperties({ withById: true });
  const canWrite = useContext(ProfileRightsContext);
  const [Confirm, openConfirm] = useConfirm();
  const [pendingBulk, setPendingBulk] = useState<PendingBulk | null>(null);
  const [runningStaticIds, setRunningStaticIds] = useState<string[]>([]);
  const { sort, toggleSort, directionFor } = useTableSort<"nom">(
    "massAutoActions_sort",
  );

  const displayedTenants = sort
    ? [...tenants].sort(
        (a, b) =>
          (sort.direction === "asc" ? 1 : -1) *
          (a.nom ?? "").localeCompare(b.nom ?? "", "fr", {
            sensitivity: "base",
          }),
      )
    : tenants;

  function isActionEligible(tenant: Tenant, staticId: string): boolean {
    // Le back refuse la résiliation de bail sans date de fin : inutile de l'envoyer en masse.
    if (staticId === AutoActionType.RESILIATION_BAIL && !tenant.fin) {
      return false;
    }
    return (
      canWrite(propertiesById[tenant.id_bien]?.id_profile_nom_owner) &&
      isActionAllowed(tenant, staticId)
    );
  }

  function getMissingActions(staticIds: string[]): BulkActionItem[] {
    const actions: BulkActionItem[] = [];
    for (const tenant of tenants) {
      for (const staticId of staticIds) {
        if (!isActionEligible(tenant, staticId)) {
          continue;
        }
        const alreadyEnabled = autoActionsAssociations[tenant.id]?.some(
          (action: BulkActionItem) => action.nom_auto_action === staticId,
        );
        if (!alreadyEnabled) {
          actions.push({ id_tenant: tenant.id, nom_auto_action: staticId });
        }
      }
    }
    return actions;
  }

  function getActiveActions(staticIds: string[]): BulkActionItem[] {
    const actions: BulkActionItem[] = [];
    for (const tenant of tenants) {
      if (!canWrite(propertiesById[tenant.id_bien]?.id_profile_nom_owner)) {
        continue;
      }
      for (const staticId of staticIds) {
        const alreadyEnabled = autoActionsAssociations[tenant.id]?.some(
          (action: BulkActionItem) => action.nom_auto_action === staticId,
        );
        if (alreadyEnabled) {
          actions.push({ id_tenant: tenant.id, nom_auto_action: staticId });
        }
      }
    }
    return actions;
  }

  const allStaticIds = autoActions.map((autoAction) => autoAction.static_id);
  const missingAll = getMissingActions(allStaticIds);

  const [isBulkRunning, runBulk] = useLoader(
    async (mode: BulkMode, staticIds: string[]) => {
      const actions =
        mode === "activate"
          ? getMissingActions(staticIds)
          : getActiveActions(staticIds);
      if (actions.length === 0) {
        return;
      }
      setRunningStaticIds(staticIds);
      try {
        if (mode === "activate") {
          await bulkCreateAutoActions(actions);
        } else {
          await bulkDeleteAutoActions(actions);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setRunningStaticIds([]);
      }
    },
  );

  function openBulkConfirm(mode: BulkMode, staticIds: string[]) {
    const count =
      mode === "activate"
        ? getMissingActions(staticIds).length
        : getActiveActions(staticIds).length;
    setPendingBulk({ mode, count });
    openConfirm({ mode, staticIds });
  }

  async function togglerHander(
    e: ChangeEvent<HTMLInputElement>,
    tenantId: number,
    actionName: string,
    actionId: number | undefined,
  ) {
    try {
      if (e.target.checked) {
        await createAutoAction(tenantId, {
          nom_auto_action: actionName,
        });
      } else {
        await deleteAutoAction(tenantId, actionId);
      }
      refresh();
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Card>
      <Confirm
        onAnswer={(confirmed: boolean, input: BulkConfirmInput) =>
          confirmed && runBulk(input.mode, input.staticIds)
        }
      >
        {pendingBulk?.mode === "deactivate"
          ? `Désactiver les ${pendingBulk.count} automatisations actives des locataires affichés ?`
          : `Activer les ${pendingBulk?.count} automatisations manquantes des locataires affichés ?`}
      </Confirm>
      <div className={styles.bulkBar}>
        <Button
          variant="secondary"
          size="sm"
          isLoading={isBulkRunning}
          disabled={missingAll.length === 0}
          onClick={() => openBulkConfirm("activate", allStaticIds)}
        >
          Tout activer
        </Button>
      </div>
      <Table variant="card" stickyFirstColumn className={styles.table}>
        <Table.Header>
          <Table.Column
            sortDirection={directionFor("nom")}
            onSort={() => toggleSort("nom")}
          >
            Locataire
          </Table.Column>
          {autoActions.map((autoAction, i) => {
            const missing = getMissingActions([autoAction.static_id]);
            const hasEligibleTenant = tenants.some((tenant) =>
              isActionEligible(tenant, autoAction.static_id),
            );
            const { icon: Icon, label } = actionTitles[autoAction.static_id];
            return (
              <Table.Column key={i} align="center">
                <Tooltip
                  content={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: getFormattedCommentaires(autoAction),
                      }}
                    ></div>
                  }
                >
                  <div>
                    <Icon />
                    <Text
                      variant="deci"
                      weight="medium"
                      className={styles.header}
                    >
                      {label}
                    </Text>
                  </div>
                </Tooltip>
                {hasEligibleTenant && (
                  <div className={styles.columnToggle}>
                    <Tooltip
                      content={
                        missing.length === 0
                          ? "Désactiver pour tous les locataires affichés"
                          : "Activer pour tous les locataires affichés"
                      }
                    >
                      <ToggleSwitch
                        status={missing.length === 0}
                        isLoading={runningStaticIds.includes(
                          autoAction.static_id,
                        )}
                        disabled={isBulkRunning}
                        hasApiCall={false}
                        onToggle={() =>
                          openBulkConfirm(
                            missing.length > 0 ? "activate" : "deactivate",
                            [autoAction.static_id],
                          )
                        }
                      />
                    </Tooltip>
                  </div>
                )}
              </Table.Column>
            );
          })}
        </Table.Header>
        <Table.Body>
          {displayedTenants.map((tenant, i) => (
            <Table.Row key={i}>
              <Table.Cell>
                <TenantPropertyCell
                  tenant={tenant}
                  property={propertiesById[tenant.id_bien]}
                />
              </Table.Cell>
              {autoActions.map((autoAction, i) => {
                const tenantAutoAction = autoActionsAssociations[
                  tenant.id
                ]?.find(
                  (action: BulkActionItem) =>
                    action.nom_auto_action === autoAction.static_id,
                );
                const checked = !!tenantAutoAction;
                return (
                  <Table.Cell key={i} align="center">
                    {isActionAllowed(tenant, autoAction.static_id) && (
                      <ToggleSwitch
                        status={checked}
                        onToggle={(e: ChangeEvent<HTMLInputElement>) =>
                          togglerHander(
                            e,
                            tenant.id,
                            autoAction.static_id,
                            tenantAutoAction?.id,
                          )
                        }
                        requiresWrite
                        profileId={
                          propertiesById[tenant.id_bien]?.id_profile_nom_owner
                        }
                      />
                    )}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Card>
  );
}
